import { Controller } from "@hotwired/stimulus"

// Phase 2.10 Tier 1 — Document Audit drawer.
//
// The drawer is rendered hidden by default; callers reveal it by
// dispatching the `document-audit-drawer:open` window event with
// `{ kind, id }` detail OR by calling `controller.open()` directly.
// T2 wires the kebab-menu "Audit trail" item to fire the event; T1
// keeps the controller purely structural so tests can render the
// partial without a click target.
export default class extends Controller {
  static targets = [
    "root",
    "backdrop",
    "tabVersions",
    "tabAccess",
    "tabApprovals",
    "tabChanges",
    "versionsPane",
    "accessPane",
    "approvalsPane",
    "changesPane",
  ]

  static values = {
    targetKind: String,
    targetId: Number,
  }

  connect() {
    this._onOpen = (event) => {
      const detail = event.detail || {}
      // Only respond to events targeting this drawer instance — the
      // page may render multiple drawers (one per file row in T2).
      if (
        detail.kind &&
        detail.id &&
        (detail.kind !== this.targetKindValue ||
          Number(detail.id) !== this.targetIdValue)
      ) {
        return
      }
      this.open()
    }
    window.addEventListener("document-audit-drawer:open", this._onOpen)
  }

  disconnect() {
    window.removeEventListener("document-audit-drawer:open", this._onOpen)
  }

  open() {
    this.rootTarget.classList.remove("hidden")
  }

  close(event) {
    if (event) event.preventDefault()
    this.rootTarget.classList.add("hidden")
  }

  showVersions() {
    this.versionsPaneTarget.classList.remove("hidden")
    this.accessPaneTarget.classList.add("hidden")
    if (this.hasApprovalsPaneTarget) this.approvalsPaneTarget.classList.add("hidden")
    if (this.hasChangesPaneTarget) this.changesPaneTarget.classList.add("hidden")
    this._activate(this.tabVersionsTarget)
  }

  // Builder SOV (Phelipe 2026-10-23): the line-by-line change history, who
  // changed which line, what and when. Its own tab, open first.
  showChanges() {
    if (!this.hasChangesPaneTarget) return
    this.changesPaneTarget.classList.remove("hidden")
    this.versionsPaneTarget.classList.add("hidden")
    this.accessPaneTarget.classList.add("hidden")
    if (this.hasApprovalsPaneTarget) this.approvalsPaneTarget.classList.add("hidden")
    this._activate(this.tabChangesTarget)
  }

  showAccess() {
    this.accessPaneTarget.classList.remove("hidden")
    this.versionsPaneTarget.classList.add("hidden")
    if (this.hasChangesPaneTarget) this.changesPaneTarget.classList.add("hidden")
    if (this.hasApprovalsPaneTarget) this.approvalsPaneTarget.classList.add("hidden")
    this._activate(this.tabAccessTarget)
  }

  // Phase 2.10 Tier 3 — Approvals tab surfaces § 2.9
  // approval_history_logs alongside § 2.10 document_access_logs in
  // the same drawer. Only renders when the parent partial detected
  // an active approval request for this target.
  showApprovals() {
    if (!this.hasApprovalsPaneTarget) return
    this.approvalsPaneTarget.classList.remove("hidden")
    this.versionsPaneTarget.classList.add("hidden")
    this.accessPaneTarget.classList.add("hidden")
    if (this.hasChangesPaneTarget) this.changesPaneTarget.classList.add("hidden")
    this._activate(this.tabApprovalsTarget)
  }

  // Phase 2.10 Tier 3 — Compare button on a version row dispatches
  // the `pdf-compare:open` window event; the diff drawer's
  // `pdf_compare_controller.js` listens, fetches the URL pair from
  // the server, and boots Apryse WebViewer compare mode.
  openCompare(event) {
    if (event) event.preventDefault()
    const params = event.params || {}
    const leftId  = params.leftId  || event.currentTarget?.dataset?.documentAuditDrawerLeftIdParam
    const rightId = params.rightId || event.currentTarget?.dataset?.documentAuditDrawerRightIdParam
    if (!leftId || !rightId) return

    window.dispatchEvent(
      new CustomEvent("pdf-compare:open", {
        detail: {
          targetType: this.targetKindValue,
          targetId: this.targetIdValue,
          leftVersionId: Number(leftId),
          rightVersionId: Number(rightId)
        }
      })
    )
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  _activate(activeTab) {
    const tabs = [this.tabVersionsTarget, this.tabAccessTarget]
    if (this.hasTabApprovalsTarget) tabs.push(this.tabApprovalsTarget)
    if (this.hasTabChangesTarget) tabs.push(this.tabChangesTarget)
    tabs.forEach((tab) => {
      if (tab === activeTab) {
        tab.classList.add("border-brand", "text-brand")
        tab.classList.remove("border-transparent", "text-gray-500")
      } else {
        tab.classList.add("border-transparent", "text-gray-500")
        tab.classList.remove("border-brand", "text-brand")
      }
    })
  }

}
